import { useState } from 'react'

import { Dialog } from '~/components/dialog'
import { Working } from '~/components/working'
import { saidOf, useWork, type Work } from '~/components/use-work'

/**
 * The prompt that stands between somebody and work they would not get back. It always names
 * what is lost, per `/specs/app.spec.md` — an action that discards nothing recorded never gets
 * one of these, so seeing one always means there is something to lose.
 *
 * A confirmation whose press is work to wait on — [onConfirm] handing back a promise — stays
 * open while it runs, with its button working, and nothing puts it away until the work is
 * done: not Cancel, not the backdrop, not Escape. The caller closes it once the work is done;
 * a failure leaves it open, its button ready again, and says in it what went wrong — the words
 * a [Refused] carries, or a plain "did not go through" for any other failure. Said anywhere else
 * — on the page under it, in a toast — it is said behind the very dialog being looked at. See
 * `/specs/app.spec.md`, A press that waits.
 */
export function ConfirmDialog({
  title,
  body,
  confirm,
  onConfirm,
  onDismiss,
  alt,
  onAlt,
}: {
  title: string
  body: string
  confirm: string
  onConfirm: () => unknown
  onDismiss: () => void
  /** A way out that loses nothing — "Save and leave" beside "Discard". */
  alt?: string | undefined
  onAlt?: (() => unknown) | undefined
}) {
  const confirming = useWork()
  const alting = useWork()
  const working = confirming.working || alting.working
  /**
   * Why the last press did not go through, and which press that was. It stands until the next
   * press has an answer rather than going the moment it is pressed — gone and back, the line
   * would shrink the dialog under the pointer for the length of every try — and the count
   * makes a failure said twice a new line, so it is announced again.
   */
  const [trouble, setTrouble] = useState<{ said: string; attempt: number } | null>(null)
  const press = (work: Work, act: () => unknown) => {
    if (working) {
      return
    }
    // A press the work's own guard turned away ran nothing, and has no answer to clear a line by.
    let ran = false
    void work
      .run(async () => {
        ran = true
        await act()
      })
      .then(
        () => {
          if (ran) {
            setTrouble(null)
          }
        },
        (error: unknown) => {
          setTrouble((was) => ({
            said: saidOf(error),
            attempt: (was?.attempt ?? 0) + 1,
          }))
        },
      )
  }
  const leave = () => {
    if (!working) {
      onDismiss()
    }
  }
  return (
    <Dialog label={title} onDismiss={leave}>
      <h2 className="text-lg font-medium">{title}</h2>
      <p className="text-sm text-ink-muted">{body}</p>
      {trouble !== null && (
        <p key={trouble.attempt} role="alert" className="text-sm text-danger">
          {trouble.said}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-3">
        <button
          type="button"
          aria-disabled={working || undefined}
          className="rounded-full px-4 py-2 text-sm"
          onClick={leave}
        >
          Cancel
        </button>
        {alt !== undefined && onAlt !== undefined ? (
          <button
            type="button"
            aria-busy={alting.working || undefined}
            className="relative rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-ink"
            onClick={() => {
              press(alting, onAlt)
            }}
          >
            <Working shown={alting.shown}>{alt}</Working>
          </button>
        ) : null}
        <button
          type="button"
          aria-busy={confirming.working || undefined}
          className="relative rounded-full bg-surface-bright px-4 py-2 text-sm font-medium text-danger"
          onClick={() => {
            press(confirming, onConfirm)
          }}
        >
          <Working shown={confirming.shown}>{confirm}</Working>
        </button>
      </div>
    </Dialog>
  )
}
